/**
 * An account's picture, in three flavours and with nothing uploaded.
 *
 *   identicon — derived from the wallet address, so every account has one from
 *               the moment it exists and nobody starts as a grey circle.
 *   preset    — the Elyxr mark recoloured to a tier. One PNG, used as a CSS
 *               mask, so six presets cost six strings and no extra bytes.
 *   token     — the logo of a token this account has actually pulled. The API
 *               checks that claim; this only draws it.
 *
 * No upload path is a deliberate choice, not a missing feature: uploads mean
 * storage, resizing, a moderation queue and an NSFW surface, and none of that
 * buys anything this product needs. If it is wanted later it slots in as a
 * fourth `kind` and nothing here has to move.
 */
import { useState } from 'react'

import { logoPath } from './AssetLogo'
import { TIER_ACCENT } from './ui'
import type { Tier } from '../lib/api'

export type AvatarKind = 'identicon' | 'preset' | 'token'

export interface Identity {
  username: string | null
  handle: string
  avatarKind: AvatarKind
  avatarRef: string | null
  /** Address, for seeding the identicon. Null on private accounts. */
  seed?: string | null
  private?: boolean
}

export const AVATAR_PRESETS = [
  'flask-starter',
  'flask-pro',
  'flask-silver',
  'flask-elite',
  'flask-gold',
  'flask-diamond',
] as const

const PRESET_TIER: Record<string, Tier> = {
  'flask-starter': 'starter',
  'flask-pro': 'pro',
  'flask-silver': 'silver',
  'flask-elite': 'elite',
  'flask-gold': 'gold',
  'flask-diamond': 'diamond',
}

/* ------------------------------------------------------------------ *
 * Identicon
 * ------------------------------------------------------------------ */

/** FNV-1a. Small, fast, and stable across browsers — which is the whole point. */
function hash(input: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

/**
 * A 5×5 symmetric block identicon, GitHub-style.
 *
 * Mirrored about the vertical axis because asymmetric noise reads as a glitch
 * while a symmetric pattern reads as a mark. Hue comes off the same hash, with
 * saturation and lightness pinned so every avatar sits at the same weight
 * against the page and none of them can come out near-black on a dark UI.
 */
function Identicon({ seed, size }: { seed: string; size: number }) {
  const h = hash(seed.toLowerCase())
  const hue = h % 360
  const fill = `hsl(${hue} 62% 58%)`
  const ground = `hsl(${hue} 30% 12%)`

  const cells: Array<[number, number]> = []
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 5; row++) {
      // One bit per cell, taken from a value re-hashed per column so the two
      // halves of the grid do not share a bit pattern.
      if ((hash(`${seed}:${col}:${row}`) & 1) === 0) continue
      cells.push([col, row])
      if (col < 2) cells.push([4 - col, row])
    }
  }

  return (
    <svg viewBox="0 0 5 5" width={size} height={size} aria-hidden shapeRendering="crispEdges">
      <rect width="5" height="5" fill={ground} />
      {cells.map(([col, row]) => (
        <rect key={`${col}-${row}`} x={col} y={row} width="1" height="1" fill={fill} />
      ))}
    </svg>
  )
}

/* ------------------------------------------------------------------ *
 * Avatar
 * ------------------------------------------------------------------ */

export function Avatar({
  identity,
  size = 32,
  className = '',
}: {
  identity: Identity
  size?: number
  className?: string
}) {
  const [failed, setFailed] = useState(false)

  const frame = (
    children: React.ReactNode,
    style?: React.CSSProperties,
  ) => (
    <span
      className={`relative grid shrink-0 place-items-center overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        background: 'var(--color-surface-3)',
        border: '1px solid var(--color-line)',
        ...style,
      }}
    >
      {children}
    </span>
  )

  // A private account has no address to seed from, so it gets a fixed glyph
  // rather than a generated one — two anonymous entries in the ticker must not
  // be distinguishable from each other.
  if (identity.private) {
    return frame(
      <svg viewBox="0 0 24 24" width={size * 0.55} height={size * 0.55} aria-hidden>
        <path
          d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 9a8 8 0 0 1 16 0"
          fill="none"
          stroke="var(--color-faint)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>,
    )
  }

  if (identity.avatarKind === 'preset' && identity.avatarRef) {
    const tier = PRESET_TIER[identity.avatarRef]
    const accent = tier ? TIER_ACCENT[tier] : 'var(--color-brand)'
    return frame(
      // The mark as a mask rather than an <img>, which is what lets one asset
      // be recoloured per tier instead of shipping six tinted copies.
      <span
        aria-hidden
        style={{
          width: size * 0.74,
          height: size * 0.74,
          background: accent,
          maskImage: 'url(/brand/exlyr-mark-256.png)',
          WebkitMaskImage: 'url(/brand/exlyr-mark-256.png)',
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        }}
      />,
      { background: `color-mix(in srgb, ${accent} 12%, var(--color-surface-3))` },
    )
  }

  if (identity.avatarKind === 'token' && identity.avatarRef && !failed) {
    // Prefixed with the asset's real kind — 'coin' or 'crypto' — because
    // that is also which logo directory the file lives in.
    const [assetKind, symbol] = identity.avatarRef.split(':', 2) as [string, string]
    return frame(
      <img
        src={logoPath(assetKind, symbol)}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className="size-full object-contain"
        style={{ padding: Math.round(size * 0.14) }}
      />,
    )
  }

  // Identicon, and the landing place for a token whose logo 404s.
  return frame(<Identicon seed={identity.seed ?? identity.handle} size={size} />)
}

/**
 * Avatar plus handle, linked to the profile when there is one to link to.
 *
 * Used by the ticker, the leaderboard and the profile header, so those three
 * can never drift on how a person is presented — or on whether a private
 * account is accidentally made clickable.
 */
export function IdentityChip({
  identity,
  size = 24,
  className = '',
  bold = false,
}: {
  identity: Identity
  size?: number
  className?: string
  bold?: boolean
}) {
  const label = (
    <>
      <Avatar identity={identity} size={size} />
      <span className={`truncate ${bold ? 'font-semibold' : 'font-medium'}`}>{identity.handle}</span>
    </>
  )

  if (!identity.username || identity.private) {
    return (
      <span
        className={`flex min-w-0 items-center gap-2 ${className}`}
        style={{ color: 'var(--color-muted)' }}
      >
        {label}
      </span>
    )
  }

  return (
    <a
      href={`/u/${identity.username}`}
      className={`flex min-w-0 items-center gap-2 hover:underline ${className}`}
    >
      {label}
    </a>
  )
}
